import type { FulfillmentType, ReleaseStrategy, ServiceRequestType } from '@/lib/shared-types';
import { submitGuestWrite, type ReplayKeyField, type Submitted } from './outbox';

/**
 * The **only** place this app writes to the API.
 *
 * A guest is on venue Wi-Fi, in a basement, behind a captive portal, or walking
 * out of range mid-tap. `api.post` from a screen means the mutation lives for
 * exactly as long as that promise: a dropped connection loses the order and the
 * guest is left with no idea whether the kitchen heard. Routing every write
 * through the outbox makes it durable *before* the network is touched,
 * replayable under one `operationId`, and bound to the table it was taken at.
 *
 * This is the same file as `mobile/customer-app/src/lib/mutations.ts`, endpoint
 * for endpoint and replay key for replay key, because it is the same product
 * talking to the same API. Change one, change both.
 *
 * Reads stay on `api.get` — there is nothing to lose by failing a read. So does
 * `POST /coupons/validate`, which is a dry run: a question whose answer nobody
 * will be around to read is worse queued than failed.
 *
 * **Where the replay key travels** matters, because the API's global
 * `ValidationPipe` runs `forbidNonWhitelisted`: an `operationId` in a body whose
 * DTO does not declare one is a 400, not an ignored extra. So each wrapper
 * below names its channel from the DTO it is actually calling, and the outbox
 * additionally sends `X-Operation-Id` on every request regardless.
 */

export type { Submitted } from './outbox';

interface Options {
  /** Reuse a key so a deliberate retry is a replay and not a second write. */
  operationId?: string;
}

const enqueue = <T>(
  url: string,
  body: unknown,
  label: string,
  replayField: ReplayKeyField,
  options?: Options,
): Promise<Submitted<T>> =>
  submitGuestWrite<T>({ method: 'post', url, body, label, replayField, ...options });

/*
 * There is no PATCH wrapper any more. The one guest write that was a PATCH set
 * another guest's spending policy on `/participants/:id`; both the route and the
 * authority behind it went with Party, and nothing a guest may now do edits a
 * record that already exists.
 */

// ------------------------------------------------------------------- orders

export interface OrderLinePayload {
  itemId: string;
  variantId?: string;
  quantity: number;
  notes?: string;
  modifierIds?: string[];
  /** The chosen options inside a meal, replayed as their own lines. */
  components?: { itemId: string; variantId?: string; quantity?: number; notes?: string }[];
}

export interface PlaceOrderPayload {
  fulfillmentType: FulfillmentType | string;
  releaseStrategy?: ReleaseStrategy | string;
  scheduledFor?: string;
  items: OrderLinePayload[];
}

/**
 * The guest never names its session or its check. The `GuestScopeGuard`
 * overwrites `sessionId` from the token and deletes `checkId` outright, so
 * sending them would be theatre and sending a *wrong* one changes nothing. No
 * price is sent either: every line is priced from the catalog server-side.
 *
 * `submit: true` because a DRAFT order is a basket nobody at the venue ever
 * sees — not a kitchen, not a waiter, not the till. What `submit` no longer
 * decides is where it lands: at a venue running waiter confirmation the API
 * parks the order at CONFIRMED for a waiter to read back at the table, and only
 * their send reaches a kitchen. The value stays `true` either way, because it
 * says the one thing the guest is actually asserting — "this is my order, take
 * it" — and the venue's own settings decide what happens next.
 */
export function placeOrder(payload: PlaceOrderPayload, options?: Options) {
  return enqueue<{ id: string; number: string }>(
    '/orders',
    { ...payload, submit: true },
    'order',
    // CreateOrderDto declares `operationId`.
    'operationId',
    options,
  );
}

// --------------------------------------------------------- service requests

export function raiseServiceRequest(type: ServiceRequestType | string, options?: Options) {
  // CreateServiceRequestDto declares `operationId`; `sessionId` is bound from
  // the token, so it is not sent.
  return enqueue<{ id: string }>(
    '/service-requests',
    { type },
    `service:${type}`,
    'operationId',
    options,
  );
}

// ----------------------------------------------------------------- feedback

export interface FeedbackPayload {
  rating: number;
  comment?: string;
}

/**
 * The guest rates the visit.
 *
 * Header channel. `POST /feedback` was given the replay guard every other guest
 * mutation already had: `FeedbackController.create` reads `@OperationId()` and
 * `FeedbackService.create` runs through `runOnce`, so a response lost on the way
 * back replays the original row instead of filing the rating twice. The key
 * travels in `X-Operation-Id`, which the outbox sends on every request anyway —
 * `CreateFeedbackDto` now declares `operationId` too, so either channel works.
 *
 * `branchId` and `sessionId` are bound from the token and `orderId` is deleted
 * for a guest, so the rating and the comment are the whole payload.
 */
export function submitFeedback(payload: FeedbackPayload, options?: Options) {
  return enqueue<{ id: string }>('/feedback', payload, 'feedback', 'header', options);
}
